import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Space, Input, Select, Tag, Modal, Form,
  message, Tooltip, InputNumber, Descriptions
} from 'antd';
import {
  PlusOutlined, SearchOutlined, EditOutlined, EyeOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

const { Search } = Input;
const { Option } = Select;

interface Material {
  priceId: number;
  mpn: string;
  tpn: string;
  partName: string;
  description: string;
  category: string;
  currentPrice: number;
  currency: string;
  unit: string;
  supplier: string;
  source: 'manual' | 'srm';
  updatedAt: string;
}

const Materials: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [searchParams, setSearchParams] = useState<any>({});
  const [modalVisible, setModalVisible] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(null);
  const [form] = Form.useForm();

  const fetchMaterials = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        ...searchParams
      });
      
      const res = await fetch(`/api/materials?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const data = await res.json();
      if (data.success) {
        setMaterials(data.data.list);
        setPagination({ ...pagination, page, pageSize, total: data.data.pagination.total });
      }
    } catch (error) {
      message.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMaterials();
  }, [searchParams]);

  const handleSearch = (keyword: string) => {
    setSearchParams((prev: any) => ({ ...prev, keyword }));
  };

  const handleCreate = () => {
    setEditingMaterial(null);
    form.resetFields();
    form.setFieldsValue({
      currency: 'USD',
      unit: 'pcs'
    });
    setModalVisible(true);
  };

  const handleEdit = (record: Material) => {
    setEditingMaterial(record);
    form.setFieldsValue({
      mpn: record.mpn,
      tpn: record.tpn,
      partName: record.partName,
      description: record.description,
      category: record.category,
      currentPrice: record.currentPrice,
      currency: record.currency,
      unit: record.unit,
      supplier: record.supplier
    });
    setModalVisible(true);
  };

  const handleView = (record: Material) => {
    setSelectedMaterial(record);
    setDetailVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      const res = await fetch('/api/materials', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          mpn: values.mpn,
          tpn: values.tpn,
          partName: values.partName,
          description: values.description,
          category: values.category,
          currentPrice: values.currentPrice,
          currency: values.currency,
          unit: values.unit,
          supplier: values.supplier
        })
      });

      const data = await res.json();
      if (data.success) {
        message.success(t('common.success'));
        setModalVisible(false);
        fetchMaterials(pagination.page, pagination.pageSize);
      } else {
        message.error(data.message || t('common.error'));
      }
    } catch (error) {
      message.error(t('common.error'));
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'PCB': 'blue',
      'Connector': 'green',
      'IC': 'purple',
      'Capacitor': 'cyan',
      'Resistor': 'orange',
      'Mechanical': 'geekblue',
      'Package': 'gold'
    };
    return colors[category] || 'default';
  };

  const columns = [
    {
      title: 'MPN',
      dataIndex: 'mpn',
      key: 'mpn',
      width: 120,
      render: (v: string) => v || '-'
    },
    {
      title: 'TPN',
      dataIndex: 'tpn',
      key: 'tpn',
      width: 120,
      render: (v: string) => v || '-'
    },
    {
      title: t('materials.partName'),
      dataIndex: 'partName',
      key: 'partName',
      width: 200,
      ellipsis: true
    },
    {
      title: t('materials.category'),
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (v: string) => v ? <Tag color={getCategoryColor(v)}>{v}</Tag> : '-'
    },
    {
      title: t('materials.currentPrice'),
      dataIndex: 'currentPrice',
      key: 'currentPrice',
      width: 120,
      render: (v: number, record: Material) => (
        <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
          {record.currency} {v?.toFixed(4)}
        </span>
      )
    },
    {
      title: t('materials.unit'),
      dataIndex: 'unit',
      key: 'unit',
      width: 80
    },
    {
      title: t('materials.supplier'),
      dataIndex: 'supplier',
      key: 'supplier',
      width: 150,
      ellipsis: true,
      render: (v: string) => v || '-'
    },
    {
      title: t('materials.source'),
      dataIndex: 'source',
      key: 'source',
      width: 80,
      render: (v: string) => (
        <Tag color={v === 'srm' ? 'green' : 'blue'}>
          {v === 'srm' ? 'SRM' : 'Manual'}
        </Tag>
      )
    },
    {
      title: t('common.updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 150,
      render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm')
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: Material) => (
        <Space size="small">
          <Tooltip title={t('common.edit')}>
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title={t('common.edit')}>
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleView(record)} />
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Search
            placeholder={`${t('common.search')} MPN/TPN/${t('materials.partName')}`}
            allowClear
            onSearch={handleSearch}
            style={{ width: 300 }}
            prefix={<SearchOutlined />}
          />
          <Select
            style={{ width: 120 }}
            placeholder={t('materials.category')}
            allowClear
            onChange={(v) => setSearchParams((prev: any) => ({ ...prev, category: v }))}
          >
            <Option value="PCB">PCB</Option>
            <Option value="Connector">Connector</Option>
            <Option value="IC">IC</Option>
            <Option value="Capacitor">Capacitor</Option>
            <Option value="Resistor">Resistor</Option>
            <Option value="Mechanical">Mechanical</Option>
            <Option value="Package">Package</Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={() => fetchMaterials()}>Refresh</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            {t('materials.addMaterial')}
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={materials}
          rowKey="priceId"
          loading={loading}
          scroll={{ x: 1300 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} items`,
            onChange: (page, pageSize) => fetchMaterials(page, pageSize)
          }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingMaterial ? t('materials.editMaterial') : t('materials.addMaterial')}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Space style={{ width: '100%' }} size="large">
            <Form.Item name="mpn" label="MPN" style={{ width: 250 }}>
              <Input placeholder="Manufacturer Part Number" />
            </Form.Item>
            <Form.Item name="tpn" label="TPN" style={{ width: 250 }}>
              <Input placeholder="Trade Part Number" />
            </Form.Item>
          </Space>
          <Form.Item
            name="partName"
            label={t('materials.partName')}
            rules={[{ required: true, message: `${t('common.required')}` }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label={t('project.description')}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Space style={{ width: '100%' }} size="large">
            <Form.Item name="category" label={t('materials.category')} style={{ width: 250 }}>
              <Select allowClear>
                <Option value="PCB">PCB</Option>
                <Option value="Connector">Connector</Option>
                <Option value="IC">IC</Option>
                <Option value="Capacitor">Capacitor</Option>
                <Option value="Resistor">Resistor</Option>
                <Option value="Mechanical">Mechanical</Option>
                <Option value="Package">Package</Option>
              </Select>
            </Form.Item>
            <Form.Item name="supplier" label={t('materials.supplier')} style={{ width: 250 }}>
              <Input />
            </Form.Item>
          </Space>
          <Space style={{ width: '100%' }} size="large">
            <Form.Item
              name="currentPrice"
              label={t('materials.currentPrice')}
              rules={[{ required: true, message: `${t('common.required')}` }]}
              style={{ width: 250 }}
            >
              <InputNumber min={0} step={0.01} precision={4} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="currency" label={t('materials.currency')} style={{ width: 120 }}>
              <Select>
                <Option value="USD">USD</Option>
                <Option value="CNY">CNY</Option>
                <Option value="THB">THB</Option>
                <Option value="MXN">MXN</Option>
              </Select>
            </Form.Item>
          </Space>
          <Form.Item name="unit" label={t('materials.unit')} style={{ width: 150 }}>
            <Select>
              <Option value="pcs">pcs</Option>
              <Option value="kg">kg</Option>
              <Option value="m">m</Option>
              <Option value="set">set</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail Modal */}
      <Modal
        title={`${t('materials.material')} - ${selectedMaterial?.mpn || selectedMaterial?.tpn}`}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={500}
      >
        {selectedMaterial && (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="MPN">{selectedMaterial.mpn || '-'}</Descriptions.Item>
            <Descriptions.Item label="TPN">{selectedMaterial.tpn || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('materials.partName')} span={2}>{selectedMaterial.partName}</Descriptions.Item>
            <Descriptions.Item label={t('materials.category')}>
              <Tag color={getCategoryColor(selectedMaterial.category)}>{selectedMaterial.category}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('materials.supplier')}>{selectedMaterial.supplier || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('materials.currentPrice')}>
              <span style={{ fontWeight: 'bold', color: '#1890ff', fontSize: 16 }}>
                {selectedMaterial.currency} {selectedMaterial.currentPrice?.toFixed(4)}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label={t('materials.unit')}>{selectedMaterial.unit}</Descriptions.Item>
            <Descriptions.Item label={t('project.description')} span={2}>{selectedMaterial.description || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('common.updatedAt')} span={2}>
              {dayjs(selectedMaterial.updatedAt).format('YYYY-MM-DD HH:mm')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default Materials;
